import { useState } from 'react';
import { Input } from '@/components/ui/input';

const COUNTRY_CODES = [
  { code: '91', label: '+91', name: 'India' },
  { code: '1', label: '+1', name: 'US / Canada' },
  { code: '44', label: '+44', name: 'UK' },
  { code: '971', label: '+971', name: 'UAE' },
  { code: '61', label: '+61', name: 'Australia' },
  { code: '65', label: '+65', name: 'Singapore' },
  { code: '60', label: '+60', name: 'Malaysia' },
  { code: '49', label: '+49', name: 'Germany' },
  { code: '33', label: '+33', name: 'France' },
] as const;

type CountryCode = (typeof COUNTRY_CODES)[number]['code'];

interface PhoneInputProps {
  id?: string;
  /** Local number digits only (no country code). This is the stored and sent value. */
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const selectCls =
  'h-9 shrink-0 rounded-lg border border-input bg-background px-2 text-xs text-foreground ' +
  'focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';

/**
 * Country-code selector + digits-only phone input.
 * The parent receives and stores only the local number digits (e.g. "9916582480").
 * Country code is UI-only and helps the user understand which format to enter.
 */
export function PhoneInput({ id, value, onChange, placeholder = 'Phone number', disabled }: PhoneInputProps) {
  const [countryCode, setCountryCode] = useState<CountryCode>('91');

  const handleDigits = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.replace(/\D/g, ''));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    if (
      [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
      ].includes(e.key)
    )
      return;
    if (/^\d$/.test(e.key)) return;
    e.preventDefault();
  };

  return (
    <div className="flex gap-1.5">
      <select
        value={countryCode}
        onChange={(e) => setCountryCode(e.target.value as CountryCode)}
        disabled={disabled}
        className={selectCls}
        aria-label="Country code"
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label} {c.name}
          </option>
        ))}
      </select>
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        value={value}
        onChange={handleDigits}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="flex-1"
      />
    </div>
  );
}
